import { useCallback, useEffect, useState } from 'react';
import { contentApi } from '../services/contentApi';
import { NavMenuItem } from '../types/content';

export const NAV_CHANGED_EVENT = 'idcoder-nav-changed';

const CACHE_KEY = 'idcoder-public-nav';

export type PublicNavState = {
  items: NavMenuItem[];
  hasPublishedEvents: boolean;
  hasPublishedGigs: boolean;
};

function readCache(): PublicNavState | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PublicNavState;
    if (!Array.isArray(parsed.items)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(state: PublicNavState) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
}

export function notifyPublicNavChanged() {
  window.dispatchEvent(new Event(NAV_CHANGED_EVENT));
}

export function usePublicNav() {
  const cached = readCache();
  const [navItems, setNavItems] = useState<NavMenuItem[]>(cached?.items ?? []);
  const [hasPublishedEvents, setHasPublishedEvents] = useState(!!cached?.hasPublishedEvents);
  const [hasPublishedGigs, setHasPublishedGigs] = useState(!!cached?.hasPublishedGigs);

  const load = useCallback(() => {
    contentApi
      .getNav()
      .then((r) => {
        const items = r.data.items || [];
        const previous = readCache();
        if (items.length === 0 && (previous?.items.length ?? 0) > 0) {
          return;
        }
        const next: PublicNavState = {
          items,
          hasPublishedEvents: !!r.data.hasPublishedEvents,
          hasPublishedGigs: !!r.data.hasPublishedGigs,
        };
        writeCache(next);
        setNavItems(next.items);
        setHasPublishedEvents(next.hasPublishedEvents);
        setHasPublishedGigs(next.hasPublishedGigs);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(NAV_CHANGED_EVENT, load);
    return () => window.removeEventListener(NAV_CHANGED_EVENT, load);
  }, [load]);

  return { navItems, hasPublishedEvents, hasPublishedGigs };
}
